import React, { useRef } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import SiteTable from '../widgets/SiteTableView';
import ScheduleCalendarTab from './ScheduleCalendarTab';
import type useSiteDashboard from '../hooks/useSiteDashboard';
import type { Project } from '../../../models/Project';

interface SiteListTabProps {
  dash: ReturnType<typeof useSiteDashboard>;
  /** 우측 일정 캘린더의 일정 등록용 전체 현장 목록. */
  allSites: Project[];
}

/**
 * 현장 목록 탭 — 좌: 현장 테이블(검색/정렬/페이지네이션/편집/삭제/맵 진입),
 * 우: 전체 현장 통합 일정 캘린더.
 *
 * 좌우 분할이 1600px 부터였는데, 1920 모니터라도 배율 125%면 CSS 폭은 1536px 라
 * 실제로는 세로로 쌓여 있었다(현장 지적). 분할 기준을 두 단계로 낮춘다:
 *
 *  - 1400px+ : 목록에 920px 를 확보한다(표 minWidth 900 + 테두리 여유라 가로
 *    스크롤이 뜨지 않는다 — 900 으로 딱 맞추면 2px 때문에 스크롤바가 생긴다).
 *  - 1150px+ : 목록이 900px 에 못 미쳐 표 안에 가로 스크롤이 생기지만, 캘린더를
 *    옆에 두는 쪽을 택한다 — 1280·1366 노트북에서도 같이 봐야 한다.
 *  - 그 아래 : 세로로 쌓는다. 여기서 더 쪼개면 캘린더 한 주가 안 들어간다.
 *
 * 각 현장의 다음 일정은 여전히 테이블 각 행의 "다음 일정" 열(NextScheduleBadge)
 * 로도 바로 보인다 — 캘린더는 전체 현장을 한눈에 보는 용도.
 *
 * 신규 등록/편집/삭제 다이얼로그는 상위 SiteDashboardView 가 소유(헤더·테이블
 * 양쪽에서 트리거되므로). 이 탭은 표시만 담당.
 */
const SiteListTab: React.FC<SiteListTabProps> = ({ dash, allSites }) => {
  // SiteRow 가 ref Map 을 요구하므로 더미 ref 유지 (scroll-to 기능 제거됨).
  const rowRefs = useRef<Map<string, HTMLElement>>(new Map());

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 3,
        alignItems: 'start',
        gridTemplateColumns: 'minmax(0, 1fr)',
        '@media (min-width: 1150px)': {
          gridTemplateColumns: 'minmax(0, 1.5fr) minmax(360px, 1fr)',
        },
        '@media (min-width: 1400px)': {
          gridTemplateColumns: 'minmax(920px, 1.4fr) minmax(400px, 1fr)',
        },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1, minHeight: 40 }}
        >
          <Typography variant="h6" fontWeight={700}>
            현장 목록
          </Typography>
        </Stack>

        {dash.listError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => dash.refresh()}>
                다시 시도
              </Button>
            }
          >
            {dash.listError}
          </Alert>
        )}

        <SiteTable
          rows={dash.rows}
          triggerDisabled={false}
          page={dash.page}
          pageSize={dash.pageSize}
          totalItems={dash.totalItems}
          sortBy={dash.sortBy}
          sortDir={dash.sortDir}
          onPageChange={dash.onPageChange}
          onSortChange={dash.onSortChange}
          onTrigger={() => undefined}
          onRowClick={dash.navigateToMap}
          onCreate={dash.openCreate}
          onEdit={dash.openEdit}
          onDelete={dash.openDelete}
          target="company"
          rowRefs={rowRefs}
          mode="manage"
        />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <ScheduleCalendarTab sites={allSites} />
      </Box>
    </Box>
  );
};

export default SiteListTab;
